<template>
    <div class="participant-admin mb-3 mt-page">

        <b-row>
            <b-col>
                <h1>
                    {{ $i18n.gt('HEADING_MANAGE_PARTICIPANTS') }}
                </h1>
            </b-col>
            <b-col class="text-right"
                   v-if="$user.roleIs('ADMIN')">
                <router-link class="btn btn-ghost-blue mr-1"
                             to="/participant/create">
                    <shs-icon name="plus"></shs-icon>
                    {{ $i18n.gt('LABEL_PARTICIPANT_NEW_PERSON') }}
                </router-link>
                <router-link to="/participant/merge" class="btn btn-ghost-blue">
                    <shs-icon name="people"></shs-icon>
                    {{ $i18n.gt('LABEL_MERGE_PARTICIPANTS') }}
                </router-link>
            </b-col>
        </b-row>

        <data-table ref="dataTable"
                    :show-filters="true"
                    :show-pager="true"
                    :show-details="false"
                    :get-items="getParticipants"
                    :filters="filters"
                    :fields="fields">

            <!-- template for custom columns -->
            <template v-slot:customField="slotProps">
                <div v-if="slotProps.row.field.key == 'user.email'">
                    {{ slotProps.row.item.user.email }}
                    <b-badge variant="danger" class="ml-3"
                             v-if="slotProps.row.item.disabled">
                        {{ $i18n.gt('LABEL_DISABLED') }}
                    </b-badge>
                </div>
                <div v-else-if="slotProps.row.field.key == 'plan.name'">
                    {{ slotProps.row.item.plan ? slotProps.row.item.plan.name : '' }}
                </div>
                <div v-else-if="slotProps.row.field.key == 'lastActiveTime'">
                    <timestamp :date="slotProps.row.item.lastActiveTime"></timestamp>
                </div>
                <div v-else-if="slotProps.row.field.key == 'actions'">
                    <template v-if="!$user.roleIs('COMPANY_ADMIN') && !$user.roleIs('MULTI_COMPANY_ADMIN')">
                        <router-link class="btn btn-secondary btn-sm mr-2 btn-icon"
                                     :to="getEditRoute(slotProps.row.item)">
                            <shs-icon name="pencil"></shs-icon>
                        </router-link>
                        <router-link class="btn btn-secondary btn-sm mr-2 btn-icon"
                                     :to="getProviderListRoute(slotProps.row.item)">
                            <shs-icon name="people"></shs-icon>
                        </router-link>
                    </template>
                    <b-btn size="sm"
                           class="mr-2 btn-icon"
                           @click="maskAsUser(slotProps.row.item.user.id)">
                        <shs-icon name="account-login"></shs-icon>
                    </b-btn>
                    <template v-if="!$user.roleIs('COMPANY_ADMIN') && !$user.roleIs('MULTI_COMPANY_ADMIN')">
                        <b-btn size="sm btn-icon"
                               variant="danger"
                               @click="deleteParticipant(slotProps.row.item)">
                            <shs-icon name="delete"></shs-icon>
                        </b-btn>
                    </template>
                </div>
                <div v-else>
                    {{ slotProps.row.item[slotProps.row.field.key] }}
                </div>
            </template>

        </data-table>

    </div>
</template>

<script>

import Vue from 'vue'
import * as _ from 'lodash'

/**
 * Page for global admins to manage Participants
 */
export default Vue.extend({
    name: "ParticipantAdmin",
    props: {
        resellerId: {
            default: null,
            required: false,
            type: Number
        },
        companyId: {
            default: null,
            required: false,
            type: Number
        },
    },
    data() {
        return {
            plans: [],
            months: [
                {value: null, text: ''},
                {value: 1, text: 'January'},
                {value: 2, text: 'February'},
                {value: 3, text: 'March'},
                {value: 4, text: 'April'},
                {value: 5, text: 'May'},
                {value: 6, text: 'June'},
                {value: 7, text: 'July'},
                {value: 8, text: 'August'},
                {value: 9, text: 'September'},
                {value: 10, text: 'October'},
                {value: 11, text: 'November'},
                {value: 12, text: 'December'},
            ],
            fields: [
                {key: 'user.email', sortable: true, label: 'User'},
                {key: 'plan.name', sortable: true, label: 'Plan'},
                {key: 'dob', sortable: true, label: 'DOB'},
                {key: 'firstname', sortable: true, label: 'First Name'},
                {key: 'lastname', sortable: true, label: 'Last Name'},
                {key: 'lastActiveTime', sortable: true, label: 'Last Active'},
                {key: 'actions', sortable: false},
            ],
        }
    },
    computed: {
        filters() {
            return {
                'plan.id': {field: 'plan.id', type: 'select', value: null, options: this.plans, label: 'Plan'},
                email: {field: 'email', type: 'string', value: null, label: 'Email'},
                text: {field: 'text', type: 'string', value: null, label: 'First/Last Name'},
                birth_month: {
                    field: 'birth_month',
                    type: 'select',
                    value: null,
                    options: this.months,
                    label: 'Birth Month'
                },
            }
        }
    },
    created() {
        const filters = {}
        if (this.resellerId && !this.companyId) {
            filters.reseller_id = { field: 'reseller_id', type: 'number', value: this.resellerId }
        } else if (this.companyId) {
            filters.company_id = { field: 'company_id', type: 'number', value: this.companyId }
        }
        this.$api.getItems(this, 'plan', filters)
            .then((plans) => {
                this.plans = _.map(plans, (plan) => {
                    return {
                        value: plan.id, text: plan.name
                    }
                })
            })
    },
    methods: {
        getEditRoute(item) {
            if (this.resellerId && this.companyId) {
                return `/participant/resellerAdmin/${this.resellerId}/company/${this.companyId}/update/id/${item.id}`
            } else if (this.resellerId && !this.companyId) {
                return `/participant/resellerAdmin/${this.resellerId}/update/id/${item.id}`
            } else return `/participant/update/id/${item.id}`
        },
        getProviderListRoute(item) {
            if (this.resellerId && this.companyId) {
                return `/participant/resellerAdmin/${this.resellerId}/company/${this.companyId}/providerList/id/${item.id}`
            } else if (this.resellerId && !this.companyId) {
                return `/participant/resellerAdmin/${this.resellerId}/providerList/id/${item.id}`
            } else return `/participant/providerList/id/${item.id}`
        },
        getParticipants(listParams) {
            let filters = this.filters
            if (this.resellerId && !this.companyId) {
                filters.reseller_id = {field: 'reseller_id', value: this.resellerId}
            } else if (this.companyId) {
                filters.company_id = { field: 'company_id',value: this.companyId }
            }
            return this.$api.getItems(this, 'participant', filters, listParams)
        },
        async deleteParticipant(participant) {
            if (await this.$bvModal.msgBoxConfirm(`Delete patient ${participant.email} permanently from the database?`)) {
                try {
                    await this.$api.deleteItem(this, 'participant', participant)
                    this.$toasted.show('Patient deleted', {duration: 3000})
                    this.$refs.dataTable.refresh();
                } catch (e) {
                    this.$utils.handleError(e, 'Error deleting patient')
                }
            }
        },
        maskAsUser(userId) {
            this.$user.maskAsUser(userId)
        }
    },
})

</script>
